/**
 * Assign Driver Modal Component
 * Modal for assigning a driver to a vehicle
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { useAssignDriver } from '../../../hooks/useVehicles';
import { useDrivers } from '../../../hooks/useDrivers';
import { ComplianceBadge } from '../ComplianceBadge';

interface AssignDriverModalProps {
  vehicleId: string;
  open: boolean;
  onClose: () => void;
}

export function AssignDriverModal({ vehicleId, open, onClose }: AssignDriverModalProps) {
  const { mutateAsync: assignDriver, isPending } = useAssignDriver();
  const { data: driversData } = useDrivers({ status: 'active' });
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const availableDrivers = driversData?.drivers.filter((d) => d.isCompliant && !d.routeAssignment) || [];

  useEffect(() => {
    if (open) {
      setSelectedDriverId('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedDriverId) {
      setError('Please select a driver');
      return;
    }

    try {
      await assignDriver({ vehicleId, data: { driverId: selectedDriverId } });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign driver');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Driver</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Driver <span className="text-red-500">*</span>
            </label>
            {availableDrivers.length === 0 ? (
              <p className="text-sm text-gray-500">No available drivers with valid CDL</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableDrivers.map((driver) => (
                  <label
                    key={driver.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedDriverId === driver.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="driver"
                      value={driver.id}
                      checked={selectedDriverId === driver.id}
                      onChange={(e) => setSelectedDriverId(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium">
                        {driver.firstName} {driver.lastName}
                      </div>
                      <div className="text-sm text-gray-600">
                        {driver.employeeNumber} • CDL Class {driver.cdl.class}
                      </div>
                      <div className="mt-1">
                        <ComplianceBadge isCompliant={driver.isCompliant} />
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || availableDrivers.length === 0}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                'Assign Driver'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
